// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import parseImageUrl from "@insite/mobius/utilities/parseImageUrl";
import {
    useCategorySearchCollection,
    useCategorySearchDataView,
} from "@store/Framework/Store/Data/CategorySearch/CategorySearchHooks";
import Loader from "@store/Library/Components/Toolkit/Loader";
import { resultCountProps } from "@store/Themes/common/props/resultCount";
import { typographyGetNoResultsAltProps } from "@store/Themes/common/props/typography";
import { categoryListProps } from "@store/Themes/common/props/category";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import { useIsSearchPage, useSearchQuery } from "@toolkit/Framework/Store/Toolkit/Pages/ProductList/ProductListHooks";
import React, { FC } from "react";

interface Props extends WidgetProps {}

const styles = {
    noResultsText: typographyGetNoResultsAltProps({ separated: true }),
};

const CategorySearchResultsPanel: FC<Props> = () => {
    const isSearchPage = useIsSearchPage();
    const searchQuery = useSearchQuery();
    const { customSettings, extensionCatalogSettings } = useSettingsCollection();
    const hasCategorySearch = isSearchPage && (customSettings?.enableCategorySearchResults ?? true);
    const hasSearchQuery = Boolean(searchQuery?.trim());
    const defaultImagePath = extensionCatalogSettings?.notFoundCategorySmallImagePath;
    const categorySearchDataView = useCategorySearchDataView();
    const categorySearchCollection = useCategorySearchCollection();
    const categoryResults = hasCategorySearch ? categorySearchCollection.items : [];
    const isLoading = hasCategorySearch && hasSearchQuery && categorySearchDataView.isLoading;

    if (isLoading) {
        return <Loader testSelector="categorySearchResultsSpinner" />;
    }

    const resultsCount = hasCategorySearch ? categorySearchCollection.totalItemCount : 0;
    const resultsCountLabel = resultsCount === 1 ? translate("Result") : translate("Results");

    return (
        <>
            <StyledWrapper {...resultCountProps.wrapper}>
                <Typography {...resultCountProps.countText}>
                    {resultsCount} {resultsCountLabel}
                </Typography>
            </StyledWrapper>
            {categoryResults.length === 0 ? (
                <Typography {...styles.noResultsText}>{translate("There were no results found.")}</Typography>
            ) : (
                <GridContainer {...categoryListProps.gridContainer}>
                    {categoryResults.map(category => {
                        const imagePath = category.smallImagePath || defaultImagePath || "";
                        const safeImagePath = parseImageUrl(imagePath).url;
                        return (
                            <GridItem
                                key={category.url || category.id || category.title}
                                {...categoryListProps.gridItem}
                            >
                                <StyledWrapper {...categoryListProps.itemWrapper}>
                                    <Link {...categoryListProps.itemLink} href={category.url || "#"}>
                                        <StyledWrapper
                                            {...categoryListProps.itemImageWrapper}
                                            className="category-image"
                                            style={{
                                                backgroundImage: safeImagePath ? `url("${safeImagePath}")` : undefined,
                                            }}
                                        />
                                        <Typography {...categoryListProps.itemTitle} className="category-name">
                                            {category.title}
                                            {category.subtitle ? ` ${translate("in")} ${category.subtitle}` : ""}
                                            <Icon className="category-name-icon" {...categoryListProps.itemTitleIcon} />
                                        </Typography>
                                    </Link>
                                </StyledWrapper>
                            </GridItem>
                        );
                    })}
                </GridContainer>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: CategorySearchResultsPanel,
    definition: {
        group: "Product List",
        displayName: "Category Search Results Panel",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
